import { Pencil, ThumbsDown, ThumbsUp } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FeedbackModal } from './FeedbackModal';

import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useCreateFeedback } from '@/hooks/useCreateFeedback';
import { useGetFeedbackByProductId } from '@/hooks/useGetFeedbackByProductId';
import { useUpdateProductAttribute } from '@/hooks/useUpdateProductAttribute';
import { capitalize, cn } from '@/lib/utils';
import { TaxonomyGetResponse } from '@/services/TaxonomiesService';

interface AttributeItemProps {
  productId: number;

  attributeId: string;
  attributeProperty: string;
  attributeName: string;
  propertyKey: string;
  taxonomies?: TaxonomyGetResponse[];
}

export function AttributeItem({
  productId,
  attributeId,
  attributeName,
  attributeProperty,
  propertyKey,
  taxonomies,
}: AttributeItemProps) {
  const [editMode, setEditMode] = useState(false);
  const [text, setText] = useState(
    Array.isArray(attributeName)
      ? attributeName.join('\n')
      : attributeName || '',
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.VisionProduct.components.Attribute',
  });

  const taxonomy = taxonomies?.find((t) => t.property === attributeProperty);

  const formattedTaxonomyFilterValues =
    (taxonomy?.filterValues?.find((attr) => attr === attributeName)
      ? [
          attributeName,
          ...taxonomy.filterValues.filter((attr) => attr !== attributeName),
        ]
      : taxonomy?.filterValues) ?? [];

  const { updateProductAttribute, isLoading: isUpdatingProductAttribute } =
    useUpdateProductAttribute(productId);

  const { createFeedback, isLoading } = useCreateFeedback(
    productId,
    attributeId,
  );

  const { feedback } = useGetFeedbackByProductId(productId);

  const currentFeedback = feedback.find(
    (item) => item.attributeId === attributeId,
  );

  const formattedAttributeProperty = capitalize(
    attributeProperty.replaceAll?.('_', ' ')?.toLowerCase(),
  );

  const isVotedUp = Boolean(
    currentFeedback?.upVoteDate && !currentFeedback.downVoteDate,
  );

  const isVotedDown = Boolean(
    !currentFeedback?.upVoteDate && currentFeedback?.downVoteDate,
  );

  function handleChangeText(event: ChangeEvent<HTMLTextAreaElement>) {
    setText(event.target.value);
  }

  async function handleAttributeChange(value: string) {
    try {
      await updateProductAttribute({
        propertyKey: propertyKey,
        property: attributeProperty,
        propertyName: value,
      });

      toast.success(t('toast.success'));
      setEditMode(false);
    } catch {
      toast.error(t('toast.error'));
    }
  }

  function handleToggleEditMode() {
    setEditMode(!editMode);
  }

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleToggleModal = useCallback(() => {
    setIsModalOpen((state) => !state);
  }, []);

  async function saveFeedback(
    vote: 'up' | 'down',
    comments: string | undefined = '',
  ) {
    const isUp = vote === 'up';

    await createFeedback({
      productId,
      downVote: !isUp,
      upVote: isUp,
      attributeId: attributeId,
      comments,
    });
  }

  function handleVoteUp() {
    saveFeedback('up');
  }

  useEffect(() => {
    if (!editMode) {
      setText(
        Array.isArray(attributeName)
          ? attributeName.join('\n')
          : attributeName || '',
      );
    }
  }, [attributeName, editMode]);

  return (
    <>
      <div className="flex items-center gap-2 border-b border-solid border-[#C3C3C3] px-5 py-3">
        <dt className="w-[200px] text-sm font-bold">
          {formattedAttributeProperty}
        </dt>
        <dd className="flex-1 text-sm">
          {!editMode &&
            (Array.isArray(attributeName) ? (
              <ul className="list-disc space-y-1 pl-5">
                {attributeName.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              attributeName
            ))}

          {editMode && formattedTaxonomyFilterValues.length > 0 && (
            <Select
              value={attributeName}
              onValueChange={handleAttributeChange}
              disabled={isUpdatingProductAttribute}
            >
              <SelectTrigger className="max-w-[400px]">
                <SelectValue placeholder={t('selectPlaceholder')} />
              </SelectTrigger>

              <SelectContent>
                {formattedTaxonomyFilterValues?.map((value, index) => (
                  <SelectItem key={index} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {editMode && formattedTaxonomyFilterValues.length === 0 && (
            <div className="space-y-2">
              <Textarea value={text} onChange={handleChangeText} />

              <div className="flex gap-1">
                <Button
                  size="sm"
                  onClick={() => {
                    // Convert line breaks to comma-separated string for backend
                    const formattedValue = text
                      .split('\n')
                      .map((line) => line.trim())
                      .filter((line) => line.length > 0)
                      .join(', ');
                    handleAttributeChange(formattedValue);
                  }}
                  disabled={isUpdatingProductAttribute}
                >
                  {isUpdatingProductAttribute
                    ? t('savingButton')
                    : t('saveButton')}
                </Button>

                {!isUpdatingProductAttribute && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleEditMode}
                  >
                    {t('cancelButton')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </dd>

        <div className="flex items-center gap-2">
          {(formattedTaxonomyFilterValues.length ||
            (taxonomies && taxonomies.length > 0)) && (
            <button onClick={handleToggleEditMode}>
              <Pencil size={16} />
            </button>
          )}

          <button disabled={isLoading || isVotedUp}>
            <ThumbsUp
              size={16}
              className={cn(isVotedUp && 'fill-blue-500')}
              onClick={handleVoteUp}
            />
          </button>

          <button disabled={isLoading || isVotedDown}>
            <ThumbsDown
              className={cn(isVotedDown && 'fill-red-500')}
              size={16}
              onClick={handleOpenModal}
            />
          </button>
        </div>
      </div>

      <FeedbackModal
        isOpen={isModalOpen}
        attributeProperty={formattedAttributeProperty}
        isLoading={isLoading}
        onOpenChange={handleToggleModal}
        onClose={handleCloseModal}
        onFeedback={saveFeedback}
      />
    </>
  );
}
